// Profil-Nachbau F-25 Entwurf C (output/profil-team-20261014/cockpit-grafiken-20261019/SCHNITTSTELLEN-C.md 3.1): Bausteine „Ziel und
// Fortschritt“. Zielblock mit Balken und Formular, Einheiten ohne Namen, gewonnene Zeit als Arbeitstage, Gründlichkeit als Punkte,
// „Was als Nächstes hilft“, Schrittfolge und kleiner Balken. Die Bausteine rechnen nichts Fachliches und kennen keinen Betrag und
// keine Person; Sätze und Werte gibt der Aufrufer (ziel.ts, zielPlattform.ts). Farben nur aus Tokens (ziel.css, diagramm.css).
import React, { useEffect, useRef, useState } from 'react';
import { AlertTriangle, BookOpen, Building2, CalendarDays, Check, EyeOff, Flag, Target, UserRound, UsersRound, Wallet, type LucideIcon } from 'lucide-react';
import { Block } from './BerichtBausteine';
import { ZIEL } from './cockpitTexte';
import { ganzeZahl, zahl } from './format';
import './ziel.css';

export type ZielSkala = { links: string; mitte: string; rechts: string };
/** Zielblock (Entwurf vorschlag-c.html:89-105, :221-230; seiten-c.js zielBlock): Titel im Kopf, rechts davon die Aktion des Aufrufers
 *  („Ziel ändern“); große Zahl mit „von …“, darunter der Balken als Bild mit Prozent für Vorlesehilfen und die Skala. prozent null
 *  zeichnet keinen Balken (kein Ziel, Team gewählt). Über 100 % bleibt der Balken voll, die Skala nennt den echten Wert. art 'knapp'
 *  färbt den Balken wie die zweite Reihe (etwa Kostendeckung unter dem Ziel). children trägt Formular oder ZielFestlegen.
 *  PF-GC-signal-1: signal ist das Wort neben der großen Zahl (etwa „unter null“ bei negativer Marge); die Zahl steht dann in der
 *  Signalfarbe, das Wort trägt die Bedeutung, damit die Farbe nicht allein trägt. */
export function ZielBlock({ id, titel, gross, von, prozent, skala, satz, fuss, art = 'gut', signal, aktion, children }: { id: string; titel: string;
  gross: string; von: string; prozent: number | null; skala?: ZielSkala; satz?: React.ReactNode; fuss?: string; art?: 'gut' | 'knapp';
  signal?: string; aktion?: React.ReactNode; children?: React.ReactNode }): JSX.Element {
  const gerundet = prozent === null ? null : Math.round(prozent);
  const teile = gerundet === null ? null : skala ?? { links: '0', mitte: ZIEL.skalaErreicht(gerundet), rechts: '' };
  return <Block id={id} titel={titel} className="ki-ziel" kopf={aktion}>
    <p className={signal ? 'ki-ziel-gross ki-signal' : 'ki-ziel-gross'}><strong>{gross}</strong>{signal && <small className="ki-signal-wort">{signal}</small>}<span>{von}</span></p>
    {prozent !== null && gerundet !== null && teile && <>
      <div className="ki-ziel-meter">
        <svg viewBox="0 0 300 22" preserveAspectRatio="none" role="img" aria-label={ZIEL.erreicht(gerundet)}>
          <rect className="ki-ziel-spur" x={0} y={3} width={300} height={16} rx={3} />
          {prozent > 0 && <rect className={art === 'knapp' ? 'ki-d-nachfragen' : 'ki-d-recherchen'} x={0} y={3}
            width={Math.max(2, Math.min(300, 3 * prozent))} height={16} rx={3} />}
        </svg>
      </div>
      <div className="ki-ziel-skala" aria-hidden="true"><span>{teile.links}</span><span>{teile.mitte}</span><span>{teile.rechts}</span></div>
    </>}
    {satz && <p className="ki-ziel-satz">{satz}</p>}
    {children}
    {fuss && <p className="ki-ziel-fuss">{fuss}</p>}
  </Block>;
}

/** Ohne gesetztes Ziel (SCHNITTSTELLEN-C 0): ein ruhiger Satz, der Beispielwert aus den Daten und nur für Besitzer und Administratoren
 *  der Knopf „Ziel festlegen“, der das Formular des Aufrufers öffnet. Ohne Recht kein Knopf. */
export function ZielFestlegen({ text, beispiel, darf, offen, formularId, onOeffnen }: { text: string; beispiel?: string; darf: boolean;
  offen: boolean; formularId: string; onOeffnen: () => void }): JSX.Element {
  return <div className="ki-ziel-festlegen">
    <p>{text}</p>
    {beispiel && <p className="ki-hinweis">{beispiel}</p>}
    {darf && <button type="button" className="adm-button" aria-expanded={offen} aria-controls={formularId} onClick={onOeffnen}>
      <Target size={16} aria-hidden="true" />{ZIEL.festlegen}</button>}
  </div>;
}

/** Formular für ein Ziel (Entwurf vorschlag-c.html:94-100, :122-130): ein Feld für eine ganze Zahl (Tausenderpunkt erlaubt),
 *  „Übernehmen“, „Abbrechen“ und mit entfernen „Ziel entfernen“. Falsche Eingaben bleiben am Feld mit demselben Satz wie am Server und
 *  rufen nichts auf. Escape und „Abbrechen“ rufen onAbbrechen; beim Öffnen bekommt das Feld den Fokus, danach setzt ihn der Aufrufer
 *  zurück auf seinen Knopf. */
export function ZielFormular({ id, label, wert, min, max, einheit, busy, fehler, entfernen, onSpeichern, onAbbrechen }: { id: string;
  label: string; wert: number | null; min: number; max: number; einheit?: string; busy: boolean; fehler?: string; entfernen?: boolean;
  onSpeichern: (wert: number | null) => void; onAbbrechen: () => void }): JSX.Element {
  const [text, setText] = useState(wert === null ? '' : zahl(wert));
  const [pruefung, setPruefung] = useState<string | null>(null);
  const feld = useRef<HTMLInputElement>(null);
  useEffect(() => { feld.current?.focus(); }, []);
  const meldung = pruefung ?? fehler ?? '';
  const fehlerId = `${id}-fehler`;
  const absenden = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const neu = ganzeZahl(text.replace(/[.\s]/g, ''));
    if (neu === null || neu < min || neu > max) { setPruefung(ZIEL.bereich(zahl(min), zahl(max))); feld.current?.focus(); return; }
    setPruefung(null);
    onSpeichern(neu);
  };
  // Escape schließt nur das Formular, nicht das Fenster der Bereichsseite (SeitenRahmen lässt ein verbrauchtes Escape liegen).
  const taste = (event: React.KeyboardEvent<HTMLFormElement>) => {
    if (event.key !== 'Escape') return;
    event.preventDefault(); event.stopPropagation();
    onAbbrechen();
  };
  return <form id={id} className="ki-ziel-form" noValidate onSubmit={absenden} onKeyDown={taste}>
    <label htmlFor={`${id}-feld`}>{label}</label>
    <div className="ki-ziel-form-zeile">
      <input ref={feld} id={`${id}-feld`} type="text" inputMode="numeric" autoComplete="off" value={text}
        aria-invalid={meldung ? true : undefined} aria-describedby={meldung ? fehlerId : undefined}
        onChange={event => { setText(event.target.value); setPruefung(null); }} />
      {einheit && <span className="ki-ziel-einheit">{einheit}</span>}
      <button type="submit" className="adm-button adm-primary" disabled={busy} aria-busy={busy || undefined}>{ZIEL.uebernehmen}</button>
      <button type="button" className="adm-button" onClick={onAbbrechen}>{ZIEL.abbrechen}</button>
      {entfernen && wert !== null && <button type="button" className="adm-button" disabled={busy} onClick={() => onSpeichern(null)}>{ZIEL.entfernen}</button>}
    </div>
    <p id={fehlerId} className="ki-ziel-fehler" role="alert">{meldung ? <><AlertTriangle size={16} aria-hidden="true" />{meldung}</> : null}</p>
  </form>;
}

/** Einheiten (Entwurf vorschlag-c.html:248-256, seiten-c.js einheiten, legende): je Person bzw. Firma ein Symbol, gefüllt für die
 *  ersten an, als ein Bild mit dem Satz des Aufrufers; darunter die Legende. Über 40 statt der Symbole ein kleiner Balken mit demselben
 *  Satz. Keine Namen, kein title. */
export function Einheiten({ label, gesamt, an, art, legende }: { label: string; gesamt: number; an: number; art: 'person' | 'firma';
  legende: { an: string; aus: string } }): JSX.Element {
  const Symbol = art === 'firma' ? Building2 : UserRound;
  const anzahl = Math.max(0, Math.round(gesamt));
  const form = art === 'firma' ? ' ki-eckig' : '';
  return <>
    {anzahl > 40
      ? <KleinMeter prozent={gesamt > 0 ? an / gesamt * 100 : 0} text={label} />
      : <ul className="ki-einheiten" role="img" aria-label={label}>
        {Array.from({ length: anzahl }, (_, i) => <li key={i} className={`ki-einheit ${i < an ? 'ki-an' : 'ki-aus'}${form}`}>
          <Symbol size={22} aria-hidden="true" /></li>)}
      </ul>}
    <p className="ki-einheiten-legende">
      <span><i className="ki-an" aria-hidden="true" />{legende.an}</span>
      <span><i className="ki-aus" aria-hidden="true" />{legende.aus}</span>
    </p>
  </>;
}

/** Gewonnene Zeit als Arbeitstage zu 8 Stunden (Entwurf vorschlag-c.html:258-273): fünf Kacheln je Reihe, Montag bis Freitag; über
 *  15 Tage zählt jede Kachel eine Woche. 5, 10 oder 15 Kacheln, gefüllt nach Anteil, als ein Bild mit dem Satz des Aufrufers. */
export function Arbeitstage({ minuten, label }: { minuten: number; label: string }): JSX.Element {
  const tage = Math.max(0, minuten) / 480;
  const wochen = tage > 15;
  const einheit = wochen ? tage / 5 : tage;
  const anzahl = Math.max(5, Math.min(15, Math.ceil(einheit / 5) * 5));
  const breite = 64, hoehe = 34, luecke = 8, jeReihe = 5, zeile = hoehe + luecke + 20;
  const reihen = Math.ceil(anzahl / jeReihe);
  return <svg className="ki-arbeitstage" viewBox={`0 0 ${jeReihe * (breite + luecke) - luecke} ${reihen * zeile}`} role="img" aria-label={label}>
    {Array.from({ length: anzahl }, (_, i) => {
      const x = (i % jeReihe) * (breite + luecke), y = Math.floor(i / jeReihe) * zeile;
      const anteil = Math.max(0, Math.min(1, einheit - i));
      return <g key={i}>
        <rect className="ki-tag-spur" x={x} y={y} width={breite} height={hoehe} rx={4} />
        {anteil > 0 && <rect className="ki-d-recherchen" x={x} y={y} width={breite * anteil} height={hoehe} rx={4} />}
        <text className="ki-d-text" x={x + breite / 2} y={y + hoehe + 17} textAnchor="middle">{wochen ? ZIEL.woche(i + 1) : ZIEL.wochentage[i % 5]}</text>
      </g>;
    })}
  </svg>;
}

/** Wie gründlich gefragt wird (Entwurf vorschlag-c.html:279-286): oben zehn Punkte für zehn Recherchen, darunter so viele Punkte, wie
 *  Nachfragen auf sie kommen (höchstens zehn), als ein Bild mit dem Satz des Aufrufers. */
export function Gruendlichkeit({ proZehn, label }: { proZehn: number; label: string }): JSX.Element {
  const nachfragen = Math.max(0, Math.min(10, Math.round(proZehn)));
  return <svg className="ki-gruendlichkeit" viewBox="0 0 260 40" role="img" aria-label={label}>
    {Array.from({ length: 10 }, (_, i) => <circle key={`r${i}`} className="ki-punkt-r" cx={9 + i * 24.5} cy={10} r={8} />)}
    {Array.from({ length: nachfragen }, (_, i) => <circle key={`n${i}`} className="ki-punkt-n" cx={9 + i * 24.5} cy={31} r={7} />)}
  </svg>;
}

export type SchrittSymbol = 'person' | 'kalender' | 'quelle' | 'ziel' | 'auge' | 'team' | 'geld' | 'achtung' | 'firma' | 'flagge';
export type Schritt = { key: string; symbol: SchrittSymbol; stark: string; text?: string; link?: React.ReactNode };
const SCHRITT_SYMBOL: Record<SchrittSymbol, LucideIcon> = {
  person: UserRound, kalender: CalendarDays, quelle: BookOpen, ziel: Target, auge: EyeOff, team: UsersRound, geld: Wallet,
  achtung: AlertTriangle, firma: Building2, flagge: Flag,
};
/** „Was als Nächstes hilft“ (Entwurf vorschlag-c.html:288-303, seiten-c.js schritte): je Schritt Symbol, ein starker Anfang, der Rest
 *  des Satzes und der fertige Link des Aufrufers auf eine vorhandene Seite. Ohne Schritte ein ruhiger Satz. */
export function Schritte({ id, schritte, leer }: { id: string; schritte: Schritt[]; leer?: string }): JSX.Element {
  return <Block id={id} titel={ZIEL.schritte} className="ki-schritte-block">
    {schritte.length > 0
      ? <ol className="ki-schritte">
        {schritte.map(schritt => {
          const Symbol = SCHRITT_SYMBOL[schritt.symbol];
          return <li key={schritt.key}>
            <Symbol size={20} aria-hidden="true" />
            <div><p><strong>{schritt.stark}</strong>{schritt.text ? ` ${schritt.text}` : ''}</p>{schritt.link}</div>
          </li>;
        })}
      </ol>
      : <p className="ki-hinweis">{leer ?? ZIEL.nichtsZuTun}</p>}
  </Block>;
}

export type Folgeschritt = { key: string; titel: string; fertig: boolean; inhalt: React.ReactNode };
/** Schrittfolge des Exports (Entwurf seiten-c.js:140-167): nummerierte Schritte, ein erledigter mit Haken und „erledigt“ für
 *  Vorlesehilfen. */
export function Schrittfolge({ label, schritte }: { label: string; schritte: Folgeschritt[] }): JSX.Element {
  return <ol className="ki-folge" aria-label={label}>
    {schritte.map((schritt, i) => <li key={schritt.key}>
      <span className={schritt.fertig ? 'ki-nummer ki-fertig' : 'ki-nummer'}>
        {schritt.fertig ? <><Check size={18} aria-hidden="true" /><span className="adm-sr-only">{ZIEL.erledigt}</span></> : i + 1}
      </span>
      <div><h3>{schritt.titel}</h3>{schritt.inhalt}</div>
    </li>)}
  </ol>;
}

/** Kleiner Balken mit Text darüber (Entwurf seiten-c.js:194-199), etwa Plätze oder Deckung je Firma; der Text trägt die Aussage, der
 *  Balken ist nur Bild. */
export function KleinMeter({ prozent, text, art = 'gut' }: { prozent: number; text: string; art?: 'gut' | 'knapp' }): JSX.Element {
  return <div className="ki-klein-meter">
    <span>{text}</span>
    <svg viewBox="0 0 100 10" preserveAspectRatio="none" aria-hidden="true" focusable="false">
      <rect className="ki-ziel-spur" x={0} y={0} width={100} height={10} />
      {prozent > 0 && <rect className={art === 'knapp' ? 'ki-d-nachfragen' : 'ki-d-recherchen'} x={0} y={0} width={Math.min(100, prozent)} height={10} />}
    </svg>
  </div>;
}
